import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { getApiErrorMessage } from '../lib/apiErrors.js'
import { useToast } from '../components/Toast/ToastProvider.jsx'

/**
 * Validates that an API method exists in the api object
 * @param {string} method - The API method name
 * @throws {Error} If method is invalid or not found
 */
function validateApiMethod(method) {
  if (typeof method !== 'string') {
    throw new Error(
      `Invalid API method key: expected string, got ${typeof method}`,
    )
  }
  if (!api[method]) {
    const availableMethods = Object.keys(api).join(', ')
    throw new Error(
      `API method "${method}" not found. Available methods: ${availableMethods}`,
    )
  }
  if (typeof api[method] !== 'function') {
    throw new Error(
      `API method "${method}" is not a function. Check api object definition.`,
    )
  }
}

/**
 * Custom hook for React Query mutation operations (POST, PUT, PATCH, DELETE)
 *
 * Wraps @tanstack/react-query's useMutation with:
 * - API method validation
 * - Automatic error/success toasts
 * - Automatic cache invalidation
 * - Success/error callbacks
 * - Consistent configuration defaults
 *
 * @param {Object} config - Mutation configuration
 * @param {string} config.method - API method name (e.g., 'saveLane')
 * @param {Array<string|Array>} [config.invalidateKeys=[]] - Query keys to invalidate on success
 *   - String: 'getLanes' invalidates all getLanes queries
 *   - Array: ['getLanes', { search: 'test' }] invalidates specific query
 * @param {Function} [config.onError] - Error callback
 * @param {Function} [config.onSuccess] - Success callback
 * @param {boolean} [config.showErrorToast=true] - Whether to show error toast
 * @param {boolean} [config.showSuccessToast=false] - Whether to show success toast
 * @param {string} [config.successMessage] - Custom success message
 * @param {string} [config.errorToastType='error'] - Toast type for errors
 * @param {string} [config.successToastType='success'] - Toast type for success
 * @param {...Object} config.mutationOptions - Additional React Query mutation options
 *
 * @returns {Object} React Query mutation result object with mutate() and mutateAsync() methods
 *
 * @example
 * // Simple invalidation (all queries with this key)
 * const saveLaneMutation = useApiMutation({
 *   method: 'saveLane',
 *   invalidateKeys: ['getLanes'],
 *   showSuccessToast: true,
 *   successMessage: 'Lane created successfully',
 * })
 *
 * @example
 * // Multiple keys with complex patterns
 * const saveLaneMutation = useApiMutation({
 *   method: 'saveLane',
 *   invalidateKeys: [
 *     'getLanes',                           // All getLanes queries
 *     ['getLanes', { search: 'active' }],  // Specific query with params
 *     'getMarkets',                         // Other related queries
 *   ],
 * })
 *
 * // Usage
 * saveLaneMutation.mutate({ name: 'New Lane', ... })
 *
 * @see REACT_QUERY_IMPLEMENTATION.md for detailed usage patterns
 */
export function useApiMutation(config) {
  const {
    method,
    onError,
    onSuccess,
    showErrorToast = true,
    showSuccessToast = false,
    successMessage,
    errorToastType = 'error',
    successToastType = 'success',
    invalidateKeys = [],
    ...mutationOptions
  } = config
  const queryClient = useQueryClient()

  const addToast = useToast()

  // Validate API method exists
  validateApiMethod(method)

  // Success/error handling lives in the mutation's own lifecycle callbacks
  // (NOT in a useEffect). Mutation-level callbacks are snapshotted onto the
  // Mutation in the MutationCache at mutate() time, so they run even if the
  // component that owns this hook unmounts before the request settles — e.g.
  // a modal that closes right after (or without) awaiting mutateAsync().
  // React Query also awaits onSuccess before resolving mutateAsync(), so by
  // the time `await mutateAsync()` returns, invalidation has been triggered.
  return useMutation({
    mutationFn: (payload) => api[method](payload),
    onSuccess: (data) => {
      invalidateKeys?.forEach((key) => {
        // Support both string keys and array keys
        // String: 'getLanes' → ['getLanes']
        // Array: ['getLanes', { search }] → use as-is
        const queryKey = typeof key === 'string' ? [key] : key
        queryClient.invalidateQueries({ queryKey })
      })
      if (showSuccessToast) {
        addToast(
          successMessage || 'Operation completed successfully',
          successToastType,
        )
      }
      onSuccess?.(data)
    },
    onError: (error) => {
      if (showErrorToast) {
        const errorMessage =
          getApiErrorMessage(error) || error.message || 'Operation failed'
        addToast(errorMessage, errorToastType)
      }
      onError?.(error)
    },
    ...mutationOptions,
  })
}
